import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from '../exportUtils';
import { accountingApi } from '../../../services/accountingApi';

const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase() === 'approved' || (status || '').toLowerCase() === 'completed'
    ? { background: '#dcfce7', color: '#166534' }
    : { background: '#fef9c3', color: '#854d0e' };
  return (
    <span style={{ ...s, padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {status || 'Pending'}
    </span>
  );
};

const inputStyle = {
  width: '100%',
  padding: '0.45rem 0.75rem',
  border: '1px solid var(--acc-border)',
  borderRadius: '4px',
  fontSize: '0.85rem',
  outline: 'none',
  boxSizing: 'border-box',
  background: 'var(--acc-surface)',
  color: 'var(--acc-text-main)',
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const Transfer = () => {
  const [transfers, setTransfers]   = useState([]);
  const [accounts, setAccounts]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [search, setSearch]         = useState('');
  const [showModal, setShowModal]   = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [form, setForm]             = useState({ ref: '', from_account_id: '', to_account_id: '', amount: '', date: todayStr(), note: '' });
  const [errors, setErrors]         = useState({});
  const exportRef = useRef(null);

  const loadData = () => {
    setLoading(true);
    Promise.all([accountingApi.getTransfers(), accountingApi.getAccounts()])
      .then(([trRes, acRes]) => {
        const toArr = r => Array.isArray(r) ? r : Array.isArray(r.data) ? r.data : Array.isArray(r.data?.data) ? r.data.data : [];
        setTransfers(toArr(trRes));
        setAccounts(toArr(acRes));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setShowExport(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openModal = () => {
    setForm({ ref: '', from_account_id: '', to_account_id: '', amount: '', date: todayStr(), note: '' });
    setErrors({});
    setShowModal(true);
  };

  const validate = () => {
    const e = {};
    if (!form.from_account_id)                         e.from   = true;
    if (!form.to_account_id)                           e.to     = true;
    if (!form.amount || parseFloat(form.amount) <= 0)  e.amount = true;
    if (!form.date)                                    e.date   = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await accountingApi.createTransfer({
        ref_no:          form.ref,
        from_account_id: form.from_account_id,
        from_account:    form.from_account_id,
        to_account_id:   form.to_account_id,
        to_account:      form.to_account_id,
        amount:          parseFloat(form.amount),
        transfer_date:   form.date,
        note:            form.note,
        additional_notes: form.note,
      });
      setShowModal(false);
      toast.success('Transfer saved successfully!');
      loadData();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transfer?')) return;
    try {
      await accountingApi.deleteTransfer(id);
      toast.success('Transfer deleted successfully!');
      loadData();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  const accountName = (id, fallback) => {
    if (fallback) return fallback;
    const a = accounts.find(x => String(x.id) === String(id));
    return a ? a.name : `Account #${id}`;
  };

  const filtered = transfers.filter(t => {
    const fromName = (accountName(t.from_account_id || t.from_account, t.from_account_name) || '').toLowerCase();
    const toName   = (accountName(t.to_account_id || t.to_account, t.to_account_name) || '').toLowerCase();
    const note     = (t.note || t.additional_notes || '').toLowerCase();
    const ref      = String(t.ref_no || t.ref || t.id || '').toLowerCase();
    const s        = search.toLowerCase();
    return fromName.includes(s) || toName.includes(s) || note.includes(s) || ref.includes(s);
  });

  const totalAmount = transfers.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

  const handleExport = (format) => {
    if (filtered.length === 0) {
      toast.warning('No data to export');
      setShowExport(false);
      return;
    }
    const headers = ['Transfer #', 'Date', 'From Account', 'To Account', 'Amount', 'Note', 'Status'];
    const rows = filtered.map(t => [
      t.id,
      t.operation_date || t.transfer_date || t.date || '—',
      accountName(t.from_account_id || t.from_account, t.from_account_name),
      accountName(t.to_account_id || t.to_account, t.to_account_name),
      `$${(parseFloat(t.amount) || 0).toFixed(2)}`,
      t.note || t.additional_notes || '—',
      t.status || 'Pending',
    ]);

    if (format === 'CSV') exportToCSV(headers, rows, 'Fund_Transfers.csv');
    else if (format === 'Excel') exportToExcel(headers, rows, 'Fund_Transfers.xlsx');
    else if (format === 'PDF') exportToPDF(headers, rows, 'Fund Transfers Report');
    else if (format === 'Print') printPage(headers, rows, 'Fund Transfers');
    setShowExport(false);
  };

  if (loading && transfers.length === 0) return <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading transfers...</div>;
  if (error) return <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>Error: {error}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      
      {/* ── Stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
        {[
          { label: 'Total Transfers', value: transfers.length, color: 'blue' },
          { label: 'Completed', value: transfers.filter(t => ['approved','completed'].includes((t.status||'').toLowerCase())).length, color: 'green' },
          { label: 'Pending',   value: transfers.filter(t => !['approved','completed'].includes((t.status||'').toLowerCase())).length, color: 'orange' },
          { label: 'Total Amount', value: `$${totalAmount.toLocaleString()}`, color: 'purple' },
        ].map(s => (
          <div key={s.label} className={`acc-stat-card ${s.color}`} style={{ padding: '1.25rem 1.35rem', gap: '0.4rem', borderRadius: '12px', minHeight: '90px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="acc-stat-label" style={{ fontSize: '0.82rem', letterSpacing: '0.04em' }}>{s.label}</div>
            <div className="acc-stat-value" style={{ fontSize: '1.65rem', fontWeight: 800, lineHeight: '1.2' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── Table ── */}
      <div className="table-content-card" style={{ padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <h3 className="acc-card-title" style={{ margin: 0 }}>Fund Transfers</h3>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            {/* Search */}
            <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--acc-surface)', padding: '0.2rem 0.5rem', borderRadius: 'var(--acc-radius)', border: '1px solid var(--acc-border)', boxShadow: 'var(--acc-shadow-sm)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--acc-text-muted)', marginLeft: '0.5rem' }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="tabs-search-input"
                placeholder="Search transfers..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ border: 'none', outline: 'none', padding: '0.35rem', fontSize: '0.85rem', background: 'transparent', minWidth: '180px', color: 'var(--acc-text-main)' }}
              />
            </div>

            {/* Export */}
            <div ref={exportRef} style={{ position: 'relative' }}>
              <button
                className="acc-btn-secondary"
                onClick={() => setShowExport(p => !p)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export ▾
              </button>
              {showExport && (
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 0.5rem)', background: 'var(--acc-surface)', border: '1px solid var(--acc-border)', borderRadius: 'var(--acc-radius)', boxShadow: 'var(--acc-shadow-lg)', zIndex: 50, minWidth: '160px', overflow: 'hidden' }}>
                  {['CSV', 'Excel', 'PDF', 'Print'].map(f => (
                    <div key={f} onClick={() => handleExport(f)}
                      style={{ padding: '0.65rem 0.85rem', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--acc-text-main)', borderBottom: f !== 'Print' ? '1px solid var(--acc-border)' : 'none', transition: 'background 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--acc-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      Export {f}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Transfer */}
            <button className="acc-btn-primary" onClick={openModal}>
              <span className="plus-icon" style={{ fontSize: '1.1rem', lineHeight: 1 }}>+</span> Add Transfer
            </button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="products-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '0.65rem 0.85rem' }}>Transfer #</th>
                <th style={{ padding: '0.65rem 0.85rem' }}>Date</th>
                <th style={{ padding: '0.65rem 0.85rem' }}>From Account</th>
                <th style={{ padding: '0.65rem 0.85rem' }}>To Account</th>
                <th style={{ textAlign: 'right', padding: '0.65rem 0.85rem' }}>Amount</th>
                <th style={{ padding: '0.65rem 0.85rem' }}>Note</th>
                <th style={{ textAlign: 'center', padding: '0.65rem 0.85rem' }}>Status</th>
                <th style={{ textAlign: 'center', padding: '0.65rem 0.85rem' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id}>
                  <td style={{ padding: '0.65rem 0.85rem' }}><span style={{ fontWeight: 700, color: 'var(--etpos-primary,#2563eb)' }}>{t.id}</span></td>
                  <td style={{ padding: '0.65rem 0.85rem', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    {t.operation_date || t.transfer_date || t.date || '—'}
                  </td>
                  <td style={{ padding: '0.65rem 0.85rem' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ color: '#ef4444', fontWeight: 700 }}>↑</span>{accountName(t.from_account_id || t.from_account, t.from_account_name)}
                    </span>
                  </td>
                  <td style={{ padding: '0.65rem 0.85rem' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ color: '#10b981', fontWeight: 700 }}>↓</span>{accountName(t.to_account_id || t.to_account, t.to_account_name)}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600, padding: '0.65rem 0.85rem' }}>
                    ${(parseFloat(t.amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td style={{ padding: '0.65rem 0.85rem', fontSize: '0.85rem' }}>{t.note || t.additional_notes || '—'}</td>
                  <td style={{ textAlign: 'center', padding: '0.65rem 0.85rem' }}><StatusBadge status={t.status} /></td>
                  <td style={{ textAlign: 'center', padding: '0.65rem 0.85rem' }}>
                    <button
                      onClick={() => handleDelete(t.id)}
                      style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: '4px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.75rem', color: '#ef4444' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '2.5rem', color: '#94a3b8' }}>
                    <div style={{ fontSize: '1.8rem', marginBottom: '0.4rem' }}>🔄</div>
                    <div style={{ fontWeight: 600 }}>No transfers found</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Add Transfer Modal ── */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--acc-surface)',
              borderRadius: '8px',
              width: '95%',
              maxWidth: '480px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 50px rgba(0,0,0,0.2)'
            }}
          >
            {/* Header */}
            <div style={{ padding: '0.85rem 1.25rem', borderBottom: '1px solid var(--acc-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--acc-text-main)' }}>Add Transfer</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--acc-text-muted)', lineHeight: 1 }}>✕</button>
            </div>

            {/* Body */}
            <div style={{ padding: '1rem 1.25rem', overflowY: 'auto', flex: 1 }}>

              {/* Reference No */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.35rem', fontSize: '0.82rem', fontWeight: 600, color: 'var(--acc-text-main)' }}>
                  Reference No:
                  <span title="Auto-generated if left blank" style={{ background: '#06b6d4', color: '#fff', borderRadius: '50%', width: '14px', height: '14px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, cursor: 'help', flexShrink: 0 }}>ℹ</span>
                </label>
                <input value={form.ref} onChange={e => setForm(p => ({ ...p, ref: e.target.value }))} style={inputStyle} placeholder="Auto-generated if blank" />
              </div>

              {/* Transfer From */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.82rem', fontWeight: 600, color: 'var(--acc-text-main)' }}>
                  Transfer from:<span style={{ color: 'var(--acc-danger)' }}>*</span>
                </label>
                <select
                  value={form.from_account_id}
                  onChange={e => setForm(p => ({ ...p, from_account_id: e.target.value }))}
                  style={{ ...inputStyle, border: errors.from ? '1px solid var(--acc-danger)' : '1px solid var(--acc-border)' }}
                >
                  <option value="">Please Select</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {errors.from && <div style={{ color: 'var(--acc-danger)', fontSize: '0.75rem', marginTop: '0.2rem' }}>Please select a source account</div>}
              </div>

              {/* Transfer To */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.82rem', fontWeight: 600, color: 'var(--acc-text-main)' }}>
                  Transfer To:<span style={{ color: 'var(--acc-danger)' }}>*</span>
                </label>
                <select
                  value={form.to_account_id}
                  onChange={e => setForm(p => ({ ...p, to_account_id: e.target.value }))}
                  style={{ ...inputStyle, border: errors.to ? '1px solid var(--acc-danger)' : '1px solid var(--acc-border)' }}
                >
                  <option value="">Please Select</option>
                  {accounts.filter(a => String(a.id) !== String(form.from_account_id)).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {errors.to && <div style={{ color: 'var(--acc-danger)', fontSize: '0.75rem', marginTop: '0.2rem' }}>Please select a destination account</div>}
              </div>

              {/* Amount */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.82rem', fontWeight: 600, color: 'var(--acc-text-main)' }}>
                  Amount:<span style={{ color: 'var(--acc-danger)' }}>*</span>
                </label>
                <input
                  type="number" min="0"
                  value={form.amount}
                  onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
                  style={{ ...inputStyle, border: errors.amount ? '1px solid var(--acc-danger)' : '1px solid var(--acc-border)' }}
                  placeholder="0.00"
                />
                {errors.amount && <div style={{ color: 'var(--acc-danger)', fontSize: '0.75rem', marginTop: '0.2rem' }}>Please enter a valid amount</div>}
              </div>

              {/* Date */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.82rem', fontWeight: 600, color: 'var(--acc-text-main)' }}>
                  Date:<span style={{ color: 'var(--acc-danger)' }}>*</span>
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                  style={{ ...inputStyle, border: errors.date ? '1px solid var(--acc-danger)' : '1px solid var(--acc-border)' }}
                />
                {errors.date && <div style={{ color: 'var(--acc-danger)', fontSize: '0.75rem', marginTop: '0.2rem' }}>Please select a date</div>}
              </div>

              {/* Note */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.82rem', fontWeight: 600, color: 'var(--acc-text-main)' }}>Note</label>
                <textarea
                  value={form.note}
                  onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
                  placeholder="Note..."
                  rows={2}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '55px', fontFamily: 'inherit' }}
                />
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid var(--acc-border)', display: 'flex', justifyContent: 'center', gap: '0.75rem', background: 'var(--acc-bg)' }}>
              <button
                onClick={handleSubmit}
                disabled={saving}
                style={{ background: saving ? '#a5b4fc' : '#5b21b6', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.45rem 1.75rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                onMouseEnter={e => { if (!saving) e.target.style.background = '#4c1d95'; }}
                onMouseLeave={e => { if (!saving) e.target.style.background = saving ? '#a5b4fc' : '#5b21b6'; }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.45rem 1.75rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                onMouseEnter={e => e.target.style.background = '#1e293b'}
                onMouseLeave={e => e.target.style.background = '#334155'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transfer;
